/**
 * Tenant Context Utility
 * Handles tenant detection from *.motivsuite.online subdomains
 */

export interface TenantContext {
  tenantName: string | null;
  isTenantSubdomain: boolean;
  subdomain: string | null;
}

/**
 * Extract tenant context from the current URL
 * Supports both *.motivsuite.online (production) and *.motiv.local (development) subdomains
 */
export function getTenantContext(): TenantContext {
  if (typeof window === 'undefined') {
    return { tenantName: null, isTenantSubdomain: false, subdomain: null };
  }

  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  
  // Handle *.motivsuite.online format (production)
  if (parts.length >= 3 && parts[parts.length - 1] === 'online' && parts[parts.length - 2] === 'motivsuite') {
    const tenantName = parts[0];
    return {
      tenantName,
      isTenantSubdomain: true,
      subdomain: tenantName
    };
  }
  
  // Handle *.motiv.local format (development)
  if (parts.length >= 3 && parts[parts.length - 1] === 'local' && parts[parts.length - 2] === 'motiv') {
    const tenantName = parts[0];
    return {
      tenantName,
      isTenantSubdomain: true,
      subdomain: tenantName
    };
  }
  
  return { tenantName: null, isTenantSubdomain: false, subdomain: null };
}

/**
 * Get the current tenant name from the URL
 */
export function getCurrentTenantName(): string | null {
  return getTenantContext().tenantName;
}

/**
 * Check if we're on a tenant subdomain
 */
export function isTenantSubdomain(): boolean {
  return getTenantContext().isTenantSubdomain;
}

/**
 * Get tenant-specific API headers
 */
export function getTenantHeaders(): Record<string, string> {
  const tenantName = getCurrentTenantName();
  if (!tenantName) {
    return {};
  }
  
  return {
    'X-Tenant-Name': tenantName,
    'X-Tenant-Subdomain': tenantName
  };
}
